import { Component, Inject, inject, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

import { IncidentsService } from '@shared/services/entities/incidents.service';
import { EquipmentsService } from '@shared/services/entities/equipments.service';
import { InstrumentalsService } from '@shared/services/entities/instrumentals.service';
import { ProductsService } from '@shared/services/entities/products.service';
import { PlacesService } from '@shared/services/entities/places.service';
import { StockService } from '@shared/services/entities/stock.service';
import { DoctorsService } from '@shared/services/entities/doctors.service';
import { InterventionsService } from '@shared/services/entities/interventions.service';
import { RemissionsService } from '@shared/services/entities/remissions.service';
import { UserScopeService, UserScope, restrictToScope } from '@app/shared/services/user-scope.service';
import { AutoSelectComponent } from '@app/shared/components/auto-select/auto-select.component';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { FormDialogShellComponent } from '@shared/components/form-dialog/form-dialog-shell.component';
import { UploadDownloadService } from '@app/shared/services/upload-download.service';
import {
  Incident,
  INCIDENT_TYPES,
  INCIDENT_PRIORITIES,
  INCIDENT_STATUSES,
  INSUMO_REASONS,
  incidentStatus,
  PRIORITY_TYPES,
} from '@shared/models/entities/incident.model';

/** Valor del select para un equipo que no está en el catálogo. */
const InventoryOther = { ID: '__OTRO__' } as const;

export interface IncidentFormDialogData {
  /** Incidencia a editar (seguimiento); ausente = reportar nueva. */
  incident?: Incident;
  /** Servicios hospitalarios para el select (los carga la pantalla). */
  services: any[];
  /** Nombre del técnico/usuario que reporta (usuario logueado). */
  reporterName?: string;
}

/**
 * Diálogo de captura/seguimiento de incidencias, sobre el cascarón
 * compartido. Los campos cambian según el tipo:
 *
 *   · INSUMO: folio de intervención + motivo (2da USB / Calidad / Otro).
 *   · EQUIPO / INSTRUMENTAL / ACCESORIO: nombre + número de serie +
 *     ¿remplazo? + prioridad (Alta/Media/Baja).
 *
 * En edición además se da el seguimiento: estado y resolución.
 */
import { isValidRfidTag, rfidTagValidators, RFID_TAG_ERROR } from '@app/shared/utils/rfid';
@Component({
  selector: 'app-incident-form-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    FormDialogShellComponent,
    AutoSelectComponent,
  ],
  templateUrl: './incident-form-dialog.component.html',
  styleUrl: './incident-form-dialog.component.scss',
})
export class IncidentFormDialogComponent {
  private readonly fb                   = inject(FormBuilder);
  private readonly incidentsService     = inject(IncidentsService);
  private readonly customMessageService = inject(CustomMessageService);
  private readonly dialogRef            = inject(MatDialogRef<IncidentFormDialogComponent>);
  private readonly equipmentsService    = inject(EquipmentsService);
  private readonly instrumentalsService = inject(InstrumentalsService);
  private readonly productsService      = inject(ProductsService);
  private readonly placesService        = inject(PlacesService);
  private readonly stockService         = inject(StockService);
  private readonly doctorsService       = inject(DoctorsService);
  private readonly interventionsService = inject(InterventionsService);
  private readonly remissionsService    = inject(RemissionsService);
  private readonly userScopeService     = inject(UserScopeService);
  private readonly uploads              = inject(UploadDownloadService);

  // ─── Fotos de evidencia ───
  static readonly MAX_PHOTOS = 5;
  static readonly MAX_MB = 8;
  /** Fotos elegidas al reportar (se suben al guardar). */
  photos = signal<{ file: File; url: string }[]>([]);
  /** Fotos ya guardadas (seguimiento): enlaces firmados para verlas. */
  savedPhotos = signal<{ key: string; url: string | null }[]>([]);
  readonly maxPhotos = IncidentFormDialogComponent.MAX_PHOTOS;

  addPhotos(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    const bad = files.filter(f => !/^image\//.test(f.type) || f.size > IncidentFormDialogComponent.MAX_MB * 1024 * 1024);
    const good = files.filter(f => !bad.includes(f));
    const room = IncidentFormDialogComponent.MAX_PHOTOS - this.photos().length;
    this.photos.update(list => [...list, ...good.slice(0, Math.max(0, room)).map(file => ({ file, url: URL.createObjectURL(file) }))]);
    if (bad.length || good.length > room) {
      void this.customMessageService.open({ title: 'Fotos', type: 'warning', dismissBtn: true,
        message: `Solo imágenes de hasta ${IncidentFormDialogComponent.MAX_MB} MB y máximo ${IncidentFormDialogComponent.MAX_PHOTOS} fotos.` });
    }
  }

  removePhoto(i: number): void {
    const p = this.photos()[i];
    if (p) URL.revokeObjectURL(p.url);
    this.photos.update(list => list.filter((_, k) => k !== i));
  }

  private async loadSavedPhotos(keys: string[]): Promise<void> {
    this.savedPhotos.set(keys.map(key => ({ key, url: null })));
    const signed = await Promise.all(keys.map(async key => {
      try { return { key, url: (await this.uploads.presign(key))?.url ?? null }; } catch { return { key, url: null }; }
    }));
    this.savedPhotos.set(signed);
  }

  /** Sube las fotos elegidas a S3 (carpeta incidents/<lote>) y devuelve sus keys. */
  private async uploadPhotos(): Promise<string[]> {
    const list = this.photos();
    if (!list.length) return [];
    const batch = (crypto as any)?.randomUUID?.() ?? String(Date.now());
    const keys: string[] = [];
    for (const p of list) {
      const up = await this.uploads.upload({ path: 'incidents', folio: batch, file: p.file });
      keys.push(up.filename);
    }
    return keys;
  }
  private scope: UserScope = { places: null, specialties: null };

  /** Folios y remisiones del hospital elegido (select de Folio/Remisión). */
  folioOptions: any[] = [];
  readonly folioLabel = (o: any) => String(o?._label ?? o?.id ?? '');

  /** Hospitales (Ubicaciones tipo HOSPITAL, activas) y médicos. */
  hospitalOptions: any[] = [];
  private doctorsAll: any[] = [];
  readonly placeLabel  = (o: any) => String(o?.alias || o?.name || '');
  readonly doctorLabel = (o: any) => String(o?.name ?? '');
  /** Médicos del hospital elegido. Sin hospital no se ofrece ninguno. */
  get doctorOptions(): any[] {
    const place = this.form.getRawValue().place;
    const current = this.form.getRawValue().doctor;
    return this.doctorsAll.filter(d => {
      if (d?.id === current) return true;
      if (!place) return false;
      const places: string[] = Array.isArray(d?.places) && d.places.length ? d.places : (d?.place ? [d.place] : []);
      return places.includes(place);
    });
  }

  /** Opciones de pieza según el tipo (del catálogo correspondiente). */
  equipmentOptions:    any[] = [];
  instrumentalOptions: any[] = [];
  productOptions:      any[] = [];
  /** Verificación de la etiqueta RFID: null = sin verificar. */
  tagCheck = signal<'ok' | 'missing' | 'checking' | null>(null);
  readonly pieceLabel = (o: any) => String(o?._label ?? o?.name ?? '');

  readonly isEditing: boolean;
  saving = signal(false);

  readonly types      = INCIDENT_TYPES;
  readonly priorities = INCIDENT_PRIORITIES;
  readonly statuses   = INCIDENT_STATUSES;
  readonly reasons    = INSUMO_REASONS;
  services: any[] = [];

  form: FormGroup = this.fb.group({
    type:              ['INSUMO', Validators.required],
    interventionFolio: [''],
    hospitalService:   [''],
    reason:            [''],
    description:       ['', Validators.required],
    equipmentName:     [''],
    place:             [null, Validators.required],
    doctor:            [null],
    catalogItem:       [null],
    tagRfid:           ['', rfidTagValidators()],
    batch:             [''],
    serialNumber:      [''],
    needsReplacement:  [false],
    priority:          [''],
    status:            ['NO RESUELTO'],
    otherEquipment:    [''],
    resolution:        [''],
  });

  constructor(@Inject(MAT_DIALOG_DATA) public data: IncidentFormDialogData) {
    this.isEditing = !!data?.incident;
    this.services = data?.services ?? [];
    const inc = data?.incident;
    if (inc) {
      this.form.patchValue({
        type:              inc.type,
        interventionFolio: inc.interventionFolio ?? '',
        hospitalService:   inc.hospitalService ?? '',
        reason:            inc.reason ?? '',
        description:       inc.description,
        equipmentName:     inc.equipmentName ?? '',
        place:             inc.place ?? null,
        doctor:            inc.doctor ?? null,
        catalogItem:       inc.catalogItem ?? null,
        tagRfid:           inc.tagRfid ?? '',
        batch:             inc.batch ?? '',
        serialNumber:      inc.serialNumber ?? '',
        needsReplacement:  inc.needsReplacement ?? false,
        priority:          inc.priority ?? '',
        status:            incidentStatus(inc.status),
        resolution:        inc.resolution ?? '',
      });
      // Seguimiento: lo que se capturó al reportar NO se edita; solo el
      // estado, la resolución y (si no es insumo) la prioridad.
      // La prioridad la pone el técnico al reportar: tampoco se edita.
      const editable = new Set(['resolution']);
      Object.keys(this.form.controls).forEach(k => { if (!editable.has(k)) this.form.get(k)?.disable(); });
      // Equipo capturado a mano ("otro equipo"): se muestra como tal.
      if (String(inc.type).toUpperCase() === 'EQUIPO' && !inc.catalogItem && inc.equipmentName) {
        this.form.patchValue({ catalogItem: InventoryOther.ID, otherEquipment: inc.equipmentName }, { emitEvent: false });
      }
      // Registros viejos sin hospital: no bloquear el guardado del seguimiento.
      this.form.get('place')?.clearValidators();
      if (inc.tagRfid) this.tagCheck.set('ok');
      if (inc.photos?.length) void this.loadSavedPhotos(inc.photos);
      this.pieces.set((inc.pieces ?? []) as any[]);
    }
    // Al cambiar de tipo, la pieza elegida de otro catálogo ya no aplica
    // (y el insumo no lleva prioridad).
    this.form.get('type')?.valueChanges.subscribe(t => {
      this.form.patchValue({ catalogItem: null, equipmentName: '', otherEquipment: '', ...(PRIORITY_TYPES.includes(t) ? {} : { priority: '' }) }, { emitEvent: false });
    });
    // Si cambia el CIR, el insumo que no sea de ese servicio ya no aplica.
    this.form.get('hospitalService')?.valueChanges.subscribe(() => {
      if (this.currentType !== 'INSUMO') return;
      const item = this.form.getRawValue().catalogItem;
      if (item && !this.pieceOptions.some(o => o.id === item)) this.form.get('catalogItem')?.setValue(null);
    });
    // Si cambia el hospital, el médico de otro hospital ya no aplica.
    this.form.get('place')?.valueChanges.subscribe(() => {
      const d = this.form.getRawValue().doctor;
      if (d && !this.doctorOptions.some(x => x.id === d)) this.form.get('doctor')?.setValue(null);
      // Torre / instrumental de otro hospital ya no aplica.
      const item = this.form.getRawValue().catalogItem;
      if (item && ['EQUIPO', 'INSTRUMENTAL'].includes(this.currentType) && !this.pieceOptions.some(o => o.id === item)) {
        this.form.get('catalogItem')?.setValue(null);
      }
      void this.loadFolios();
      this.lockerStock = null; this.lockerResults.set([]);
      this.pieces.update(list => list.filter(p => p.source !== 'LOCKER'));
    });
    this.form.get('tagRfid')?.valueChanges.subscribe(() => this.tagCheck.set(null));
    if (!this.isEditing) {
      this.form.get('description')?.valueChanges.subscribe(() => this.scheduleSuggestion());
      this.form.get('reason')?.valueChanges.subscribe(() => this.scheduleSuggestion());
    }
    // Al elegir folio / remisión, los insumos se limitan a los que se usaron ahí.
    this.form.get('interventionFolio')?.valueChanges.subscribe(() => void this.loadFolioProducts());
    void this.loadCatalogs();
  }

  // ─── Insumos no conformes (tabla) ───
  /** Origen de lo que se agrega: piezas del folio / remisión o del locker del hospital. */
  pieceSource: 'FOLIO' | 'LOCKER' = 'FOLIO';
  pieces = signal<any[]>([]);
  folioPieces: any[] = [];            // piezas del folio / remisión elegido
  folioPick: string | null = null;    // pieza elegida en el selector
  lockerQuery = '';
  lockerResults = signal<any[]>([]);
  lockerSearching = signal(false);
  private lockerStock: any[] | null = null;   // piezas disponibles del hospital (se cargan una vez)
  private lockerPlace: string | null = null;
  private lockerTimer: any = null;

  readonly folioPieceLabel = (p: any) => String(p?._label ?? '');

  private pieceRow(s: any, source: 'FOLIO' | 'LOCKER'): any {
    const prod = this.productOptions.find(p => p.id === s.product);
    return { stock: s.id, product: s.product, code: prod?.code ?? '', description: prod?.description ?? '',
             batch: s.batch ?? '', tagRfid: s.tagRfid ?? '', warehouse: s.warehouse ?? null, source,
             status: s.status, origin: source === 'FOLIO' ? this.folioOriginLabel : 'Locker' };
  }

  /** "Folio TME-12" o "Remisión R-3", según lo elegido en Folio/Remisión. */
  get folioOriginLabel(): string {
    const f = String(this.form.getRawValue().interventionFolio ?? '').trim();
    const opt = this.folioOptions.find(o => String(o.id).toUpperCase() === f.toUpperCase());
    return `${opt?._kind === 'REMISION' ? 'Remisión' : 'Folio'} ${f}`;
  }

  /** Piezas del folio / remisión que todavía no están en la tabla. */
  get folioPieceOptions(): any[] {
    const taken = new Set(this.pieces().map(p => p.stock));
    return this.folioPieces.filter(p => !taken.has(p.id) && p.status !== 'No conforme');
  }

  addFolioPiece(): void {
    const s = this.folioPieces.find(p => p.id === this.folioPick);
    if (!s) return;
    this.pieces.update(list => list.some(p => p.stock === s.id) ? list : [...list, this.pieceRow(s, 'FOLIO')]);
    this.folioPick = null;
  }

  /** Buscar en los lockers del hospital por lote, RFID o código. */
  onLockerQuery(text: string): void {
    this.lockerQuery = text;
    clearTimeout(this.lockerTimer);
    this.lockerTimer = setTimeout(() => void this.searchLocker(), 300);
  }

  private async searchLocker(): Promise<void> {
    const q = this.lockerQuery.trim().toUpperCase();
    const place = this.form.getRawValue().place;
    if (q.length < 2 || !place) { this.lockerResults.set([]); return; }
    this.lockerSearching.set(true);
    try {
      if (!this.lockerStock || this.lockerPlace !== place) {
        const r: any = await this.stockService.search({ searchType: 'default', condition: { deleted: false, place, status: 'Disponible' } });
        this.lockerStock = ((r?.data ?? []) as any[]).filter(s => s?.status === 'Disponible');
        this.lockerPlace = place;
      }
      const codeOf = (s: any) => String(this.productOptions.find(p => p.id === s.product)?.code ?? '').toUpperCase();
      const taken = new Set(this.pieces().map(p => p.stock));
      this.lockerResults.set(this.lockerStock
        .filter(s => !taken.has(s.id) && (String(s.batch ?? '').toUpperCase().includes(q)
          || String(s.tagRfid ?? '').toUpperCase().includes(q) || codeOf(s).includes(q)))
        .slice(0, 25)
        .map(s => this.pieceRow(s, 'LOCKER')));
    } catch {
      this.lockerResults.set([]);
    } finally {
      this.lockerSearching.set(false);
    }
  }

  addLockerPiece(row: any): void {
    this.pieces.update(list => list.some(p => p.stock === row.stock) ? list : [...list, row]);
    this.lockerResults.update(list => list.filter(r => r.stock !== row.stock));
  }

  removePiece(i: number): void { this.pieces.update(list => list.filter((_, k) => k !== i)); }

  // ─── Clasificación sugerida (calidad / no calidad) ───
  suggestion = signal<{ qualityClass: string | null; qualityScore: number | null } | null>(null);
  private suggestTimer: any = null;
  private scheduleSuggestion(): void {
    clearTimeout(this.suggestTimer);
    const text = String(this.form.getRawValue().description ?? '').trim();
    if (text.length < 8) { this.suggestion.set(null); return; }
    this.suggestTimer = setTimeout(async () => {
      try {
        this.suggestion.set(await this.incidentsService.classify(text, this.form.getRawValue().reason || null));
      } catch { this.suggestion.set(null); }
    }, 600);
  }

  /** La prioridad solo aplica a equipo / instrumental. */
  get hasPriority(): boolean {
    return PRIORITY_TYPES.includes(this.currentType);
  }

  /** ¿Se eligió "Otro equipo" (no está en el catálogo, p. ej. no es torre)? */
  get isOtherEquipment(): boolean {
    return this.currentType === 'EQUIPO' && this.form.getRawValue().catalogItem === InventoryOther.ID;
  }

  /** Servicio (CIR) elegido: sus productos acotan la lista de insumos. */
  private get selectedService(): any {
    const id = this.form.getRawValue().hospitalService;
    return id ? this.services.find(s => s.id === id) : null;
  }

  readonly serviceLabel = (s: any) => [s?.cir, s?.name].filter(Boolean).join(' — ') || s?.epioneCode || s?.mdtCode || '';

  get currentType(): string {
    return this.form.getRawValue().type;
  }

  /** Catálogos de piezas: Equipos, Instrumental y Productos (insumos). */
  private async loadCatalogs(): Promise<void> {
    const cond = { searchType: 'default', condition: { deleted: false } };
    const safe = async (svc: any) => { try { const r: any = await svc.search(cond); return (r?.data ?? []) as any[]; } catch { return []; } };
    const [places, equipments, instrumentals, products, doctors] = await Promise.all([
      safe(this.placesService), safe(this.equipmentsService), safe(this.instrumentalsService), safe(this.productsService), safe(this.doctorsService),
    ]);
    const isActive = (x: any) => x?.active !== false && String(x?.active).toLowerCase() !== 'false';
    const current = this.data?.incident?.place;
    // Alcance del usuario (Usuarios → Parámetros): solo sus hospitales.
    try { this.scope = await this.userScopeService.load(); } catch { /* sin alcance: todos */ }
    const hospitals = places.filter((p: any) => String(p?.type ?? '').toUpperCase() === 'HOSPITAL' && isActive(p));
    this.hospitalOptions = [
      ...restrictToScope(hospitals, this.scope.places),
      ...places.filter((p: any) => p.id === current && !restrictToScope(hospitals, this.scope.places).some((h: any) => h.id === current)),
    ].sort((a: any, b: any) => String(a.alias || a.name).localeCompare(String(b.alias || b.name), 'es'));
    // Con un solo hospital asignado se preselecciona.
    if (!this.isEditing && !this.form.getRawValue().place && this.hospitalOptions.length === 1) {
      this.form.get('place')?.setValue(this.hospitalOptions[0].id);
    }
    this.doctorsAll = doctors
      .filter((d: any) => d.id === this.data?.incident?.doctor || (isActive(d) && d.status !== false))
      .sort((a: any, b: any) => String(a.name).localeCompare(String(b.name), 'es'));
    const aliasById: Record<string, string> = Object.fromEntries(places.map((p: any) => [p.id, p.alias || p.name || '']));
    const byLabel = (a: any, b: any) => a._label.localeCompare(b._label, 'es', { sensitivity: 'base', numeric: true });
    const withPlace = (x: any) => ({ ...x, _label: [x.name, aliasById[x.place]].filter(Boolean).join(' · ') });
    const active = (x: any) => x?.active !== false;
    this.equipmentOptions    = equipments.filter(active).map(withPlace).sort(byLabel);
    this.instrumentalOptions = instrumentals.filter(active).map(withPlace).sort(byLabel);
    this.productOptions      = products.filter(active)
      .map((p: any) => ({ ...p, _label: [p.code, p.description].filter(Boolean).join(' — ') })).sort(byLabel);
    await this.loadFolios();
  }

  /** Folios (intervenciones) y remisiones del hospital elegido. */
  async loadFolios(): Promise<void> {
    const place = this.form.getRawValue().place;
    const current = String(this.form.getRawValue().interventionFolio ?? '').trim();
    const opts: any[] = [];
    if (place) {
      const safe = async (p: Promise<any>) => { try { const r: any = await p; return (r?.data ?? []).filter((x: any) => x) as any[]; } catch { return []; } };
      const [folios, remissions] = await Promise.all([
        safe(this.interventionsService.search({ searchType: 'default', condition: { deleted: false, hospital: place } })),
        safe(this.remissionsService.search({ searchType: 'default', condition: { deleted: false, place } })),
      ]);
      const seen = new Set<string>();
      const push = (folio: any, label: string, kind: 'FOLIO' | 'REMISION', raw: any) => {
        const f = String(folio ?? '').trim();
        if (!f || seen.has(f.toUpperCase())) return;
        seen.add(f.toUpperCase());
        opts.push({ id: f, _label: label, _kind: kind, _raw: raw });
      };
      folios
        .sort((a, b) => String(b.createdOn ?? '').localeCompare(String(a.createdOn ?? '')))
        .forEach(i => push(i.fileNumber, ['Folio ' + i.fileNumber, i.patientName].filter(Boolean).join(' · '), 'FOLIO', i));
      remissions
        .sort((a, b) => String(b.createdOn ?? '').localeCompare(String(a.createdOn ?? '')))
        .forEach(r => push(r.folio, ['Remisión ' + r.folio, r.doctorName].filter(Boolean).join(' · '), 'REMISION', r));
    }
    // El valor ya capturado (registros viejos) siempre se conserva.
    if (current && !opts.some(o => o.id.toUpperCase() === current.toUpperCase())) opts.unshift({ id: current, _label: current });
    this.folioOptions = opts;
    if (!this.isEditing && current && !place) this.form.get('interventionFolio')?.setValue('');
    await this.loadFolioProducts();
  }

  /**
   * Insumos del folio / remisión elegido: con folio, la lista de insumos se
   * limita a lo que se usó ahí (consumo del folio o, si aún no se captura,
   * lo que se entregó; en remisión, sus renglones). null = sin folio.
   */
  folioProductIds: Set<string> | null = null;
  folioProductsLoading = false;

  async loadFolioProducts(): Promise<void> {
    const folio = String(this.form.getRawValue().interventionFolio ?? '').trim().toUpperCase();
    const opt = folio ? this.folioOptions.find(o => String(o.id).toUpperCase() === folio) : null;
    if (!opt?._raw) { this.folioProductIds = null; this.folioPieces = []; return; }
    this.folioProductsLoading = true;
    try {
      const ids = new Set<string>();
      if (opt._kind === 'REMISION') {
        for (const it of (opt._raw.items ?? []) as any[]) if (it?.product) ids.add(String(it.product));
        const remStock = ((opt._raw.items ?? []) as any[]).map(it => it?.stock).filter(Boolean);
        await this.loadFolioPieces(remStock);
      } else {
        const p = opt._raw.products ?? {};
        const pick = (v: any) => (Array.isArray(v) ? v : []).map((x: any) => (x && typeof x === 'object') ? (x.stock ?? x.id ?? x.product) : x).filter(Boolean);
        const consumption = pick(p.consumption);
        const stockIds = consumption.length ? consumption : pick(p.delivery);
        await this.loadFolioPieces([...new Set([...consumption, ...pick(p.delivery)])]);
        if (stockIds.length) {
          try {
            const r: any = await this.stockService.search({ searchType: 'default', condition: { deleted: false, id: stockIds } });
            for (const st of (r?.data ?? []) as any[]) if (st?.product) ids.add(String(st.product));
          } catch { /* sin piezas: se queda vacío */ }
        }
      }
      this.folioProductIds = ids;
      // El insumo elegido que no sea de este folio ya no aplica.
      const item = this.form.getRawValue().catalogItem;
      if (this.currentType === 'INSUMO' && item && !ids.has(String(item))) this.form.get('catalogItem')?.setValue(null);
    } finally {
      this.folioProductsLoading = false;
    }
  }

  /** Piezas del folio / remisión (para la tabla de insumos no conformes). */
  private async loadFolioPieces(ids: string[]): Promise<void> {
    this.folioPick = null;
    if (!ids.length) { this.folioPieces = []; return; }
    try {
      const r: any = await this.stockService.search({ searchType: 'default', condition: { deleted: false, id: ids } });
      this.folioPieces = ((r?.data ?? []) as any[]).map(s => {
        const prod = this.productOptions.find(p => p.id === s.product);
        return { ...s, _label: [prod?.code, s.batch ? 'Lote ' + s.batch : 'S/L', s.tagRfid, s.status !== 'Consumido' ? s.status : '']
          .filter(Boolean).join(' · ') + (prod?.description ? ' — ' + prod.description : '') };
      }).sort((a, b) => a._label.localeCompare(b._label, 'es', { numeric: true }));
    } catch { this.folioPieces = []; }
  }

  /** Opciones del select de pieza para el tipo actual. */
  get pieceOptions(): any[] {
    switch (this.currentType) {
      case 'EQUIPO':       return [...this.ofPlace(this.equipmentOptions), { id: InventoryOther.ID, _label: 'Otro equipo (no está en el catálogo)' }];
      case 'INSTRUMENTAL': return this.ofPlace(this.instrumentalOptions);
      case 'INSUMO': {
        // Con folio / remisión elegido: SOLO los insumos que se usaron ahí.
        if (this.folioProductIds) {
          const current = this.form.getRawValue().catalogItem;
          return this.productOptions.filter(p => p.id === current || this.folioProductIds!.has(String(p.id)));
        }
        // Si hay CIR elegido, solo los insumos de ese servicio. Los renglones
        // del servicio traen `code` (del Excel) y/o `product` (id del
        // catálogo); los registros viejos son una lista de ids.
        const service = this.selectedService;
        if (!service) return this.productOptions;
        const rows: any[] = Array.isArray(service.products) ? service.products : [];
        const norm = (v: any) => String(v ?? '').trim().toUpperCase();
        const ids = new Set<string>();
        const codes = new Set<string>();
        rows.forEach(r => {
          if (r && typeof r === 'object') {
            if (r.product) ids.add(String(r.product));
            if (r.code) codes.add(norm(r.code));
          } else if (r) {
            ids.add(String(r));
            codes.add(norm(r));
          }
        });
        const current = this.form.getRawValue().catalogItem;
        return this.productOptions.filter(p => p.id === current || ids.has(String(p.id)) || codes.has(norm(p.code)));
      }
      default:             return [];
    }
  }

  /** Piezas (torres / instrumental) del hospital elegido; sin hospital, ninguna. */
  private ofPlace(list: any[]): any[] {
    const place = this.form.getRawValue().place;
    const current = this.form.getRawValue().catalogItem;
    return list.filter(x => x.id === current || (!!place && x.place === place));
  }

  /** Pieza del catálogo: solo equipo / instrumental. Los insumos van en la tabla. */
  get usesCatalog(): boolean {
    return ['EQUIPO', 'INSTRUMENTAL'].includes(this.currentType);
  }

  /** Verifica que la etiqueta RFID exista en el inventario. */
  async verifyTag(): Promise<boolean> {
    const ctrl = this.form.get('tagRfid');
    const tag = String(ctrl?.value ?? '').trim().toUpperCase();
    if (!tag) { this.tagCheck.set(null); return true; }
    ctrl?.setValue(tag, { emitEvent: false });
    if (!isValidRfidTag(tag)) { this.tagCheck.set(null); ctrl?.markAsTouched(); return false; }
    this.tagCheck.set('checking');
    try {
      const r: any = await this.stockService.search({ searchType: 'default', condition: { deleted: false, tagRfid: tag } });
      const item = ((r?.data ?? []) as any[]).find(x => String(x?.tagRfid ?? '').toUpperCase() === tag);
      this.tagCheck.set(item ? 'ok' : 'missing');
      if (item?.batch) this.form.get('batch')?.setValue(String(item.batch).toUpperCase());
      return !!item;
    } catch {
      this.tagCheck.set('missing');
      return false;
    }
  }

  get isInsumo(): boolean {
    return this.form.getRawValue().type === 'INSUMO';
  }

  fieldError(field: string, error = 'required'): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl?.hasError(error) && ctrl.touched);
  }

  close(): void {
    this.dialogRef.close(false);
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }

    const v = this.form.getRawValue();
    const insumo = v.type === 'INSUMO';
    // Insumo: el producto, la etiqueta y el lote salen de la tabla de insumos.
    if (insumo && !this.isEditing && !this.pieces().length) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning',
        message: 'Agrega al menos un insumo a la tabla (del folio / remisión o del locker).', dismissBtn: true });
      return;
    }
    const tablePieces = this.pieces();
    const insumoName = tablePieces.length
      ? [...new Set(tablePieces.map(p => p.code).filter(Boolean))].join(', ') + (tablePieces[0]?.description ? ' — ' + tablePieces[0].description : '')
      : null;
    // Pieza del catálogo: se guarda el id y su etiqueta legible.
    const other = v.type === 'EQUIPO' && v.catalogItem === InventoryOther.ID;
    if (other && !String(v.otherEquipment ?? '').trim()) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'Escribe qué equipo es.', dismissBtn: true });
      return;
    }
    const piece = this.usesCatalog && !other ? this.pieceOptions.find(o => o.id === v.catalogItem) : null;
    const pieceName = insumo ? (this.isEditing ? (this.data.incident?.equipmentName ?? null) : insumoName)
      : other ? String(v.otherEquipment).trim().toUpperCase()
      : this.usesCatalog ? (piece?._label ?? (v.catalogItem ? v.equipmentName : null)) : (v.equipmentName || null);

    const payload: Incident = {
      type:              v.type,
      description:       v.description,
      hospitalService:   v.hospitalService || null,
      place:             v.place || null,
      doctor:            v.doctor || null,
      interventionFolio: insumo ? (v.interventionFolio || null) : null,
      reason:            insumo ? (v.reason || null) : null,
      equipmentName:     pieceName || null,
      catalogItem:       this.usesCatalog && !other ? (v.catalogItem || null) : null,
      // Ya no se capturan: los toma el servidor del primer insumo de la tabla.
      tagRfid:           null,
      batch:             null,
      pieces:            insumo ? this.pieces().map(p => ({ stock: p.stock, source: p.source })) : [],
      serialNumber:      insumo ? null : (v.serialNumber || null),
      needsReplacement:  insumo ? null : !!v.needsReplacement,
      // Prioridad: solo equipo / instrumental.
      priority:          PRIORITY_TYPES.includes(v.type) ? (v.priority || null) : null,
      status:            v.status || 'NO RESUELTO',
      resolution:        v.resolution || null,
      reportedByName:    this.data?.incident?.reportedByName ?? this.data?.reporterName ?? null,
    };

    this.saving.set(true);
    try {
      if (!this.isEditing && this.photos().length) {
        try {
          payload.photos = await this.uploadPhotos();
        } catch (err) {
          const msg = err instanceof Error ? err.message : '';
          throw new Error('No se pudieron subir las fotos de evidencia. ' + msg);
        }
      }
      if (this.isEditing) {
        payload.id = this.data.incident!.id;
        payload.folio = this.data.incident!.folio;
        payload.operationType = 'single' as any;
        delete (payload as any).pieces;
        if (payload.status === 'RESUELTO' && !this.data.incident!.resolvedOn) {
          payload.resolvedOn = new Date().toISOString();
        }
        await this.incidentsService.update(payload);
        await this.customMessageService.open({
          title: '¡Actualizada!', type: 'success',
          message: `Incidencia <b>${payload.folio}</b> actualizada.`, duration: 2500,
        });
      } else {
        const result: any = await this.incidentsService.create(payload);
        const folio = result?.data?.[0]?.folio ?? '';
        await this.customMessageService.open({
          title: '¡Reportada!', type: 'success',
          message: `Incidencia <b>${folio}</b> registrada correctamente.`, duration: 2500,
        });
      }
      this.dialogRef.close(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ocurrió un error al guardar la incidencia.';
      await this.customMessageService.open({ title: 'Error', type: 'error', message, dismissBtn: true });
    } finally {
      this.saving.set(false);
    }
  }
}
